import './Dashboard.sass';

import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  AnimatedEmoji,
  Button,
  Header,
  List,
  ListItem,
  Placeholder,
  ScrollView,
} from '$uikit';
import { dashboardActions, dashboardSelector } from '$store/dashboard';
import { Loader, Truncate, UnitPhoto } from '$shared/components';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import {
  DashboardNotifyModel,
  DashboardSectionModel,
  DashboardTaskModel,
} from '$store/models';
import { DashboardItem } from '$core/Dashboard/DashboardItem/DashboardItem';
import { DashboardButtons } from '$core/Dashboard/DashboardButtons/DashboardButtons';
import { IcChevron16, IcSearch28, IcStar28 } from '$assets';
import { useRoute } from 'react-router5';
import { ISOLATED_MODE, SEARCH } from '$shared/constants/pages';
import { classNames } from '$utils';
import { AdventCalendarBanner } from '$core/AdventCalendarBanner/AdventCalendarBanner';

const Notify: FC<{ notify: DashboardNotifyModel }> = ({ notify }) => {
  const Component = notify.link ? 'a' : 'div';
  const props: any = {};
  if (notify.link) {
    props.href = notify.link;
    props.target = '_blank';
  }

  return (
    <Component
      {...props}
      className={classNames({
        Dashboard__notify: true,
        [notify.color]: true,
      })}
    >
      <div className="Dashboard__notify__cont">
        <div className="Dashboard__notify__title">{notify.title}</div>
        <div className="Dashboard__notify__text">{notify.text}</div>
      </div>
      <div className="Dashboard__notify__chevron">
        <IcChevron16 />
      </div>
    </Component>
  );
};

export const Dashboard: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const { router } = useRoute();
  const user = useCurrentUserInfo();
  const { loadingStatus, sections, top, notify } = useSelector(dashboardSelector);

  const loadData = useCallback(() => {
    dispatch(dashboardActions.loadDashboard());
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const tasks = useMemo(() => {
    const result: DashboardTaskModel[] = [];
    const recurring: DashboardTaskModel[] = [];
    const sectionsMap: { [index: string]: DashboardSectionModel } = {};
    for (let section of sections) {
      for (let group of section.groups) {
        let hasCompleted = false;
        for (let task of group.tasks) {
          // if (task.isCompleted || (task.isReached && !task.isActive)) {
          //   hasCompleted = true;
          // }

          if (task.isCompleted || !task.isActive) {
            continue;
          }

          if (task.isRecurring) {
            recurring.push(task);
          } else {
            result.push(task);
          }
          sectionsMap[task.id] = section;
        }

        // if (!hasCompleted) {
        //   break;
        // }
      }
    }
    return {
      tasks: result,
      recurring,
      sectionsMap,
    };
  }, [sections]);

  function renderContent() {
    if (loadingStatus === 'loading') {
      return <Loader isWrapped />;
    }

    if (loadingStatus === 'failed') {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadData}>
              {t('failed_button')}
            </Button>
          }
          centered
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    return (
      <>
        {top < 60 && (
          <div className="Dashboard__top">
            <IcStar28 />
            <div className="Dashboard__top__text">
              <Truncate
                text={t('dashboard_top', {
                  top,
                })}
              />
            </div>
          </div>
        )}
        {!!notify && <Notify notify={notify} />}
        <DashboardButtons />
        {/* <div className="Dashboard__isolated_wrap">
          <AdventCalendarBanner
            userId={user?.id!}
            userDomain={user?.domain}
            isModel={user?.verified === 2}
            isMyProfile={true}
          />
        </div> */}
        {!!user?.isolated && (
          <div className="Dashboard__isolated_wrap">
            <List>
              <ListItem
                indicator={t(
                  user?.isolated
                    ? 'dashboard_isolated_mode_enabled'
                    : 'dashboard_isolated_mode_disabled',
                )}
                chevron
                routeName={ISOLATED_MODE}
              >
                {t('dashboard_isolated_mode')}
              </ListItem>
            </List>
          </div>
        )}
        <div className="Dashboard__tasks">
          {tasks.tasks.length || !!tasks.recurring.length ? (
            <>
              <div className="Dashboard__tasks__title">{t('dashboard_tasks_title')}</div>
              {tasks.tasks.map((task) => (
                <DashboardItem
                  key={task.id}
                  task={task}
                  section={tasks.sectionsMap[task.id]}
                />
              ))}
              {!!tasks.recurring.length && (
                <div className="Dashboard__tasks__subTitle">
                  {t('dashboard_recurring_title')}
                </div>
              )}
              {tasks.recurring.map((task) => (
                <DashboardItem
                  key={task.id}
                  task={task}
                  section={tasks.sectionsMap[task.id]}
                />
              ))}
            </>
          ) : (
            <Placeholder
              iconSize={80}
              icon={<AnimatedEmoji size={80} emoji="✅" />}
              title={t('dashboard_tasks_placeholder_title')}
            >
              {t('dashboard_tasks_placeholder_desc')}
            </Placeholder>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <Header
        leftContent={
          <UnitPhoto
            hitSlop={12}
            size="extra-small"
            unit={user}
            onlineIndicator={false}
          />
        }
        className="Dashboard__header"
        rightIcons={[
          {
            icon: <IcSearch28 />,
            onClick: () => router.navigate(SEARCH),
          },
        ]}
      >
        {t('dashboard_title')}
      </Header>
      <ScrollView>{renderContent()}</ScrollView>
    </>
  );
};
